import { useMemo } from 'react';
import { DecoratedSilo } from './types';
import { useSiloConfiguration } from '@silo/config-legacy';
import { ChecksumAddress, makeAddressSync } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';
import { useQueryMerklCampaigns } from '../use-query-merkl-campaigns';
import { useQuerySiloData } from '../../features/silo/use-query-silo-data';
import { toDecoratedSilo } from './to-decorated-silo';

interface Options {
  enabled?: boolean;
}

export interface DecoratedSiloQueryResult {
  data: DecoratedSilo | undefined;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: unknown;
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * @deprecated use granular queries instead
 *
 * Backed by the `/api/silo` server-side endpoint via `useQuerySiloData`. The
 * response is mapped to the legacy `DecoratedSilo` (BigNumber-based) shape so
 * the ~25 existing consumer sites work unchanged.
 */
export const useQueryDecoratedSilo = (
  siloAddress: ChecksumAddress | null,
  options?: Options
): DecoratedSiloQueryResult => {
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const siloConfig = siloAddress ? getConfigurationForSilo(siloAddress) : null;

  const marketAddress = siloAddress ? makeAddressSync(siloAddress) : null;

  const { data: merklCampaigns } = useQueryMerklCampaigns();

  const filteredCampaigns = useMemo(() => {
    if (!merklCampaigns || !marketAddress || !protocolInstance) return undefined;

    return merklCampaigns.filter(
      (campaign) => campaign.marketAddress === marketAddress && campaign.protocolKey === protocolInstance
    );
  }, [merklCampaigns, marketAddress, protocolInstance]);

  const enabled = (options?.enabled ?? true) && !!siloAddress && !!siloConfig && !!filteredCampaigns;

  const siloDataQuery = useQuerySiloData({ marketAddress, enabled });

  const data = useMemo(() => {
    if (!siloDataQuery.data || !siloConfig || !filteredCampaigns) return undefined;

    return toDecoratedSilo({
      siloData: siloDataQuery.data,
      siloConfig,
      merklCampaigns: filteredCampaigns,
    });
  }, [siloDataQuery.data, siloConfig, filteredCampaigns]);

  return {
    data,
    status: siloDataQuery.status,
    error: siloDataQuery.error,
    isLoading: siloDataQuery.isLoading,
    isFetching: siloDataQuery.isFetching,
  };
};

export const useQueryDecoratedSiloWithSuspense = (siloAddress: ChecksumAddress | null, enabled = true) => {
  /**
   * Not using actual suspense for now - error boundaries need to be thoroughly placed to use suspense
   */
  const { data } = useQueryDecoratedSilo(siloAddress, { enabled });

  if (!data) {
    throw new Error('data is undefined. Suspense is not working as intended.');
  }

  return data as DecoratedSilo;
};
